import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { mapBikeToComponentBike } from "../utils/bikeMappers";
import { BikeFilter } from "../components/BikesControls";

interface DashboardFilters {
  regionId: string;
  organizationId: string;
  siteId: string;
}

export const useBikesData = (locationFilters?: DashboardFilters) => {
  const [bikes, setBikes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [allNeedsMaintenanceBikes, setAllNeedsMaintenanceBikes] = useState([]);
  
  const fetchBikes = async (searchTerm = '', page = 1, filter: string | null = null) => {
    setIsLoading(true);
    try {
      // Determine which site IDs to filter by based on location filters
      let siteIdsToFilter: string[] | null = null;
      
      if (locationFilters?.siteId && locationFilters.siteId !== 'all') {
        siteIdsToFilter = [locationFilters.siteId];
      } else if (locationFilters?.organizationId && locationFilters.organizationId !== 'all') {
        // Get all sites for this organization
        const { data: orgSites } = await supabase
          .from('sites')
          .select('id')
          .eq('organization_id', locationFilters.organizationId);
        
        if (orgSites && orgSites.length > 0) {
          siteIdsToFilter = orgSites.map(site => site.id);
        }
      } else if (locationFilters?.regionId && locationFilters.regionId !== 'all') {
        // Get all organizations for this region, then all sites for those organizations
        const { data: regionOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('region_id', locationFilters.regionId);
        
        if (regionOrgs && regionOrgs.length > 0) {
          const orgIds = regionOrgs.map(org => org.id);
          const { data: regionSites } = await supabase
            .from('sites')
            .select('id')
            .in('organization_id', orgIds);
          
          if (regionSites && regionSites.length > 0) {
            siteIdsToFilter = regionSites.map(site => site.id);
          }
        }
      }

      if (filter === 'needs_maintenance') {
        // Fetch up to 500 bikes for client-side filtering and pagination
        let query = supabase
          .from('bikes')
          .select(`
            *,
            model:bike_models(name),
            site:sites(name),
            users:user_location_id(username),
            maintenance_issues(status)
          `, { count: "exact" })
          .order('created_at', { ascending: false })
          .range(0, 499);

        // Apply location filter if provided
        if (siteIdsToFilter && siteIdsToFilter.length > 0) {
          query = query.in('site_id', siteIdsToFilter);
        }

        // Apply search filter if provided
        if (searchTerm) {
          query = query.or(`serial_number.ilike.%${searchTerm}%,model.name.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query;
        if (error) {
          console.error("Error fetching bikes:", error);
          throw error;
        }

        // Client-side filter for 'needs_maintenance'
        const filteredData = (data || []).filter(bike =>
          bike.maintenance_issues?.some(
            issue => issue.status === 'reported' || issue.status === 'in_progress'
          )
        );
        setAllNeedsMaintenanceBikes(filteredData);
        setTotalItems(filteredData.length);
        setIsLoading(false);
        return;
      }

      // Build query for other filters
      let query = supabase
        .from('bikes')
        .select(`
          *,
          model:bike_models(name),
          site:sites(name),
          users:user_location_id(username),
          maintenance_issues(status)
        `, { count: "exact" })
        .order('created_at', { ascending: false });
      
      // Apply location filter if provided
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        query = query.in('site_id', siteIdsToFilter);
      }
      
      // Apply search filter if provided
      if (searchTerm) {
        query = query.or(`serial_number.ilike.%${searchTerm}%,model.name.ilike.%${searchTerm}%`);
      }
      
      // Apply status filter if provided
      if (filter) {
        if (filter === 'active' || filter === 'inactive') {
          query = query.eq('status', filter);
        } else if (filter === 'low_battery') {
          query = query.lt('battery_level', 20);
        }
      }
      
      // Calculate pagination range
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Apply pagination
      const { data, error, count } = await query.range(from, to).order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching bikes:", error);
        throw error;
      }
      
      const mappedBikes = (data || []).map(mapBikeToComponentBike);
      setBikes(mappedBikes);
      setTotalItems(count || 0);
      setIsLoading(false);
    } catch (error) {
      console.error("Error in fetchBikes:", error);
      toast.error("Failed to load bikes. Please try refreshing.");
      setIsLoading(false);
    }
  };
  
  // Memoized paginated bikes for 'needs_maintenance'
  const paginatedNeedsMaintenanceBikes = useMemo(() => {
    if (activeFilter === 'needs_maintenance') {
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      return allNeedsMaintenanceBikes.slice(start, end).map(mapBikeToComponentBike);
    }
    return [];
  }, [allNeedsMaintenanceBikes, currentPage, pageSize, activeFilter]);
  
  useEffect(() => {
    fetchBikes(searchTerm, currentPage, activeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, activeFilter, locationFilters]);
  
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to the first page when searching
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const applyFilter = (filter: BikeFilter | string) => {
    if (typeof filter === 'object') {
      if (filter.status) {
        setActiveFilter(filter.status);
      } else if (filter.batteryLevel === 'low') {
        setActiveFilter('low_battery');
      } else if (filter.maintenanceStatus) {
        setActiveFilter(filter.maintenanceStatus);
      }
    } else {
      setActiveFilter(filter);
    }
    setCurrentPage(1); // Reset to the first page when applying a filter
  };
  
  const clearFilter = () => {
    setActiveFilter(null);
    setCurrentPage(1); // Reset to the first page when clearing the filter
  };
  
  const handleExport = async () => {
    try {
      setIsLoading(true);
      
      // Determine which site IDs to filter by for export
      let siteIdsToFilter: string[] | null = null;
      
      if (locationFilters?.siteId && locationFilters.siteId !== 'all') {
        siteIdsToFilter = [locationFilters.siteId];
      } else if (locationFilters?.organizationId && locationFilters.organizationId !== 'all') {
        const { data: orgSites } = await supabase
          .from('sites')
          .select('id')
          .eq('organization_id', locationFilters.organizationId);
        
        if (orgSites && orgSites.length > 0) {
          siteIdsToFilter = orgSites.map(site => site.id);
        }
      } else if (locationFilters?.regionId && locationFilters.regionId !== 'all') {
        const { data: regionOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('region_id', locationFilters.regionId);
        
        if (regionOrgs && regionOrgs.length > 0) {
          const orgIds = regionOrgs.map(org => org.id);
          const { data: regionSites } = await supabase
            .from('sites')
            .select('id')
            .in('organization_id', orgIds);
          
          if (regionSites && regionSites.length > 0) {
            siteIdsToFilter = regionSites.map(site => site.id);
          }
        }
      }
      
      let query = supabase
        .from('bikes')
        .select(`
          *,
          model:bike_models(name),
          site:sites(name),
          users:user_location_id(username),
          maintenance_issues(status)
        `, { count: "exact" })
        .order('created_at', { ascending: false });
      
      // Apply location filter for export
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        query = query.in('site_id', siteIdsToFilter);
      }
      
      if (activeFilter) {
        if (activeFilter === 'active' || activeFilter === 'inactive') {
          query = query.eq('status', activeFilter);
        } else if (activeFilter === 'needs_maintenance') {
          // Fetch up to 500 bikes and filter client-side
          query = query.range(0, 499);
        } else if (activeFilter === 'low_battery') {
          query = query.lt('battery_level', 20);
        }
      }
      const { data, error } = await query;
      if (error) throw error;
      let exportData = data;
      if (activeFilter === 'needs_maintenance') {
        exportData = (data || []).filter(bike =>
          bike.maintenance_issues?.some(
            issue => issue.status === 'reported' || issue.status === 'in_progress'
          )
        );
      }
      const exportRows = exportData.map(bike => ({
        'Model': bike.model?.name || 'Unknown',
        'Serial Number': bike.serial_number,
        'Status': bike.status,
        'Battery Level': `${bike.battery_level || 0}%`,
        'Location': bike.site?.name || 'Unassigned',
        'Distance Traveled (km)': bike.distance_traveled_km || 0,
        'Last Updated': new Date(bike.created_at).toLocaleString(),
        'Maintenance Status': bike.maintenance_issues?.[0]?.status || 'ok'
      }));
      const headers = Object.keys(exportRows[0]);
      const csvContent = [
        headers.join(','),
        ...exportRows.map(row =>
          headers.map(header => {
            const value = row[header];
            return typeof value === 'string' && (value.includes(',') || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          }).join(',')
        )
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bikes_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Bikes exported successfully');
    } catch (error) {
      console.error('Error exporting bikes:', error);
      toast.error('Failed to export bikes');
    } finally {
      setIsLoading(false);
    }
  };
  
  const refetch = () => {
    fetchBikes(searchTerm, currentPage, activeFilter);
  };
  
  return {
    bikes: activeFilter === 'needs_maintenance' ? paginatedNeedsMaintenanceBikes : bikes,
    isLoading,
    refetch,
    searchTerm,
    handleSearch,
    currentPage,
    pageSize,
    handlePageChange,
    activeFilter,
    applyFilter,
    clearFilter,
    handleExport,
    totalItems: activeFilter === 'needs_maintenance' ? allNeedsMaintenanceBikes.length : totalItems
  };
};
